import { ArrowRight, BookOpen, Check, Handshake, Leaf, ShieldCheck, Sparkles, Store } from "lucide-react";
import { Link } from "react-router-dom";
import PublicBreadcrumbs from "../components/PublicBreadcrumbs";
import SEOHead from "../components/SEOHead";
import { buildBreadcrumbSchema, buildOrganizationSchema, buildWebsiteSchema } from "../lib/seo";

const missionItems = [
  "Menyediakan produk herbal dan alami pilihan.",
  "Memberikan informasi produk yang jelas dan tidak berlebihan.",
  "Menjaga kualitas layanan dan kepuasan pelanggan.",
  "Mendukung pola hidup sehat secara berkelanjutan.",
  "Membangun hubungan jangka panjang dengan pelanggan.",
];

const values = [
  { title: "Kualitas", icon: ShieldCheck, desc: "Produk dipilih dengan perhatian pada mutu dan kenyamanan pelanggan." },
  { title: "Kepercayaan", icon: Handshake, desc: "Kami menjaga komunikasi yang jelas dan layanan yang dapat diandalkan." },
  { title: "Edukasi", icon: BookOpen, desc: "Informasi produk disampaikan secara wajar, jelas, dan mudah dipahami." },
  { title: "Kemudahan", icon: Store, desc: "Pengalaman belanja dibuat praktis untuk kebutuhan harian keluarga." },
  { title: "Komitmen", icon: Sparkles, desc: "Kami terus mendukung pilihan hidup sehat yang sederhana dan berkelanjutan." },
];

const AboutPage = () => {
  const breadcrumbItems = [
    { name: "Beranda", path: "/" },
    { name: "Tentang", path: "/tentang" },
  ];

  const pageSchema = [
    buildOrganizationSchema(),
    buildWebsiteSchema(),
    buildBreadcrumbSchema(breadcrumbItems),
  ];

  return (
    <main className="pb-28 md:pb-0">
      <SEOHead
        title="Tentang Teras Alami"
        description="Kenali Teras Alami, penyedia produk herbal dan alami pilihan yang mendukung gaya hidup sehat dengan informasi yang jelas dan pelayanan terpercaya."
        path="/tentang"
        schema={pageSchema}
      />

      <section className="relative overflow-hidden bg-grain">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-14 sm:px-6 sm:py-20 lg:grid-cols-2 lg:px-8 lg:py-24">
          <div>
            <PublicBreadcrumbs items={breadcrumbItems} />
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700">
              <Leaf className="h-4 w-4" /> Tentang brand
            </span>
            <h1 className="mt-5 font-display text-4xl font-semibold leading-tight tracking-tight text-stone-900 sm:text-5xl lg:text-6xl">
              Tentang Teras Alami
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-stone-600">
              Teras Alami hadir sebagai pilihan produk herbal dan alami untuk mendukung kebutuhan
              harian serta gaya hidup sehat keluarga Indonesia.
            </p>
            <Link
              to="/#produk"
              className="mt-8 inline-flex h-14 items-center justify-center rounded-xl bg-emerald-600 px-6 text-base font-medium text-white transition hover:-translate-y-0.5 hover:bg-emerald-700"
            >
              Belanja Sekarang <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>

          <div className="relative">
            <div className="absolute -inset-6 rounded-[3rem] bg-emerald-100/60 blur-2xl"></div>
            <div className="relative overflow-hidden rounded-[2rem] border border-stone-200 bg-stone-100 shadow-sm aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5]">
              <img
                src="https://images.pexels.com/photos/10802278/pexels-photo-10802278.jpeg"
                alt="Produk herbal alami Teras Alami untuk gaya hidup sehat keluarga"
                className="h-full w-full object-cover"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="font-display text-2xl font-semibold text-stone-900 sm:text-3xl">
              Siapa Kami
            </h2>
            <p className="mt-4 text-base leading-8 text-stone-600 sm:text-lg">
              Teras Alami menyediakan berbagai produk herbal, alami, dan kebutuhan kesehatan harian
              yang dipilih dengan memperhatikan kualitas dan kenyamanan pelanggan. Kami percaya
              bahwa menjaga kesehatan dapat dimulai dari kebiasaan sederhana, pilihan produk yang
              tepat, dan pola hidup yang seimbang.
            </p>
          </div>

          <div className="rounded-[2rem] border border-emerald-100 bg-emerald-50 p-6 shadow-sm sm:p-8">
            <h2 className="font-display text-2xl font-semibold text-stone-900 sm:text-3xl">
              Visi
            </h2>
            <p className="mt-4 text-base leading-8 text-stone-700 sm:text-lg">
              Menjadi pilihan terpercaya masyarakat Indonesia dalam memenuhi kebutuhan produk herbal
              dan alami yang aman, nyaman, dan mudah dijangkau.
            </p>
          </div>
        </div>
      </section>

      <section className="border-y border-stone-100 bg-stone-50">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl font-semibold text-stone-900 sm:text-4xl">
              Misi Kami
            </h2>
            <p className="mt-3 text-lg leading-relaxed text-stone-600">
              Kami membangun pengalaman belanja herbal yang jelas, nyaman, dan dapat dipercaya.
            </p>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {missionItems.map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
                <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                  <Check className="h-3.5 w-3.5" />
                </span>
                <p className="text-stone-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
        <div className="max-w-3xl">
          <h2 className="font-display text-3xl font-semibold text-stone-900 sm:text-4xl">
            Nilai Kami
          </h2>
          <p className="mt-3 text-lg leading-relaxed text-stone-600">
            Prinsip yang menjaga Teras Alami tetap dekat dengan kebutuhan pelanggan.
          </p>
        </div>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {values.map((value) => {
            const Icon = value.icon;
            return (
              <article key={value.title} className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 font-display text-xl font-semibold text-stone-900">
                  {value.title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-stone-600">{value.desc}</p>
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
